// Pricing, Squad, Club registry, Case study, FAQ, Apply, Footer

import { useState as useStateP, useEffect as useEffectP, useId as useIdP } from 'react';
import { ASCII } from '../lib/ascii.js';
import { SquadObelisk } from '../components/SquadObelisk.jsx';

/* ---------- Pricing ---------- */
function PricingSection() {
  const phases = [
    { n: 'DSC', name: 'Discovery', dur: '2 weeks', price: '€5K',  note: 'assumption audit' },
    { n: 'T01', name: 'Trial 01',  dur: '1 month', price: '€15K', note: 'demand' },
    { n: 'T02', name: 'Trial 02',  dur: '2 months',price: '€30K', note: 'retention' },
    { n: 'T03', name: 'Trial 03',  dur: '3 months',price: '€45K', note: 'revenue' },
  ];
  return (
    <section id="pricing" data-screen-label="pricing">
      <div className="sec-head">
        <div className="sec-label">04 · The Investment</div>
        <div>
          <h2>One number<br/>per month. <em>Clean.</em></h2>
          <p className="lead dim" style={{marginTop:28, maxWidth:'52ch'}}>
            Flat monthly fee. No hourly billing. No scope-creep negotiations. Exit after any trial with full ownership of everything produced.
          </p>
        </div>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:0, border:'1px solid var(--border)'}} className="price-grid">
        {phases.map((p, i) => (
          <div key={i} style={{padding:'32px 28px', borderRight: i < 3 ? '1px solid var(--border)' : 'none', background: i === 3 ? 'var(--surface)' : 'var(--bg)', position:'relative'}}>
            <h3 style={{fontSize:10, letterSpacing:3, color:'var(--fg-dim)', fontWeight:700, fontFamily:'var(--mono)', margin:0}}>{p.n} · {p.name.toUpperCase()}</h3>
            <div style={{fontSize:11, color:'var(--fg-dim)', marginTop:4}}>{p.dur}</div>
            <div style={{fontFamily:'var(--mono)', fontSize:44, color:'var(--signal)', fontWeight:800, marginTop:20, letterSpacing:-1}}>{p.price}</div>
            <div style={{fontSize:11, color:'var(--fg-dim)', marginTop:6, textTransform:'uppercase', letterSpacing:1.5}}>{p.note}</div>
          </div>
        ))}
      </div>

      <div style={{marginTop: 32, padding:'32px 40px', border:'1px solid var(--border)', background:'var(--bg)', display:'grid', gridTemplateColumns:'1fr 1fr', gap:0}}>
        <div style={{paddingRight:40, borderRight:'1px solid var(--border)'}}>
          <div style={{fontSize:10, letterSpacing:3, color:'var(--signal)', fontWeight:700, textTransform:'uppercase', marginBottom:10}}>02100 · total</div>
          <div style={{fontFamily:'var(--mono)', fontSize:'clamp(40px, 5vw, 72px)', color:'var(--fg-bright)', fontWeight:800, letterSpacing:-2, lineHeight:1}}>€95,000</div>
          <div style={{fontSize:12, color:'var(--fg-dim)', marginTop:8, letterSpacing:2, textTransform:'uppercase'}}>6 months · incl. Discovery</div>
        </div>
        <div style={{paddingLeft:40}}>
          <div style={{fontSize:10, letterSpacing:3, color:'var(--fg-dim)', fontWeight:700, textTransform:'uppercase', marginBottom:10}}>Traditional equivalent</div>
          <div style={{fontFamily:'var(--mono)', fontSize:'clamp(40px, 5vw, 72px)', color:'var(--fg-dim)', fontWeight:800, letterSpacing:-2, lineHeight:1, textDecoration:'line-through', textDecorationColor:'var(--signal)', textDecorationThickness:'3px'}}>€650K–1.7M</div>
          <div style={{fontSize:12, color:'var(--fg-dim)', marginTop:8, letterSpacing:2, textTransform:'uppercase'}}>2–4 years · same question</div>
        </div>
      </div>

      {/* Equity — de-emphasized; only if you elect Club + scale path with us */}
      <div style={{marginTop: 32, border:'1px solid var(--border)', padding:'22px 28px', background:'var(--bg)'}} className="equity-grid">
        <div style={{display:'grid', gridTemplateColumns:'auto 1fr', gap: 24, alignItems:'start'}}>
          <div>
            <div style={{fontSize:10, letterSpacing:3, color:'var(--fg-dim)', fontWeight:700, textTransform:'uppercase'}}>Optional equity · post Trial 03</div>
            <div style={{fontFamily:'var(--mono)', fontSize: 28, fontWeight:700, color:'var(--fg)', lineHeight:1.2, marginTop: 6, letterSpacing:-1}}>
              12–18<span style={{color:'var(--signal)'}}>%</span>
            </div>
          </div>
          <div style={{fontSize:12, color:'var(--fg-dim)', maxWidth: '62ch', lineHeight: 1.55}}>
            Applies only if you choose to scale with 02100 and join The Club — not a default fee substitute. Band reflects strategic risk absorbed; vested at Trial 03 close against written delivery. Until then, fees only; cap table stays clean for downstream rounds.
          </div>
        </div>
      </div>

      {/* Comparison methodology footnote — CEO-review scope expansion. */}
      <div style={{marginTop: 28, padding: '20px 24px', border: '1px dashed var(--border)', background: 'var(--bg)'}}>
        <div style={{fontSize: 9, letterSpacing: 3, color: 'var(--fg-dim)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8}}>
          § Pricing methodology · footnote
        </div>
        <p className="small" style={{color: 'var(--fg-dim)', lineHeight: 1.6, maxWidth: '92ch', margin: 0, fontFamily: 'var(--mono)'}}>
          Comparison baselines are in EUR, pre-tax, excluding founder opportunity cost. <b style={{color: 'var(--fg)'}}>Traditional €650K–1.7M</b> derives from typical 18–36 month pre-product build at 3–6 FTEs, EU senior blended rate €7.5–12K/mo <span style={{color: 'var(--fg)'}}>[CB Insights 2024 startup burn survey; EU ranges]</span>. <b style={{color: 'var(--fg)'}}>YC figures</b>: <span style={{color: 'var(--fg)'}}>[YC public terms: $125K / 7% · W24]</span>. <b style={{color: 'var(--fg)'}}>Angel round equity</b>: median 15–25% seed dilution <span style={{color: 'var(--fg)'}}>[Crunchbase EU seed rounds, H2 2024, n=412]</span>. <b style={{color: 'var(--fg)'}}>DIY burn</b>: self-reported from founders who shipped without external capital <span style={{color: 'var(--fg)'}}>[Indie Hackers 2024 survey, EU respondents, n=312]</span>. Optional 02100 equity (12–18%, Club election) vests at Trial 03 close, contingent on delivery; excluded from downstream cap-table dilution mechanics until elected.
        </p>
      </div>
    </section>
  );
}

/* ---------- Squad (instrument + rail in SquadObelisk) ---------- */
function SquadSection() {
  return (
    <section id="squad" data-screen-label="squad">
      <div className="sec-head">
        <div className="sec-label">05 · The Squad</div>
        <div>
          <h2>Dedicated elite<br/><em>delivery squad.</em></h2>
          <p className="lead dim" style={{marginTop:28, maxWidth:'52ch'}}>
            Product, marketing, and engineering as one elite squad — sole assignment for your engagement, held to outcome discipline and pace. Every seat is filled by an operator with <strong style={{color:'var(--fg-bright)', fontWeight:700}}>10+ years</strong> of relevant experience; augmented by AI workflows. The same squad from Discovery through Trial Three. Names and bios after Discovery, not on a landing page.
          </p>
        </div>
      </div>

      <SquadObelisk />

      <div style={{marginTop: 24, padding: 28, border:'1px solid var(--border)', background:'var(--surface)', display:'grid', gridTemplateColumns:'1fr auto', gap: 24, alignItems:'center'}}>
        <p className="small" style={{color:'var(--fg)', margin:0, maxWidth:'none'}}>
          The squad is built for elite delivery: Club alumni who have shipped under pressure — not staff augmentation, not a rotating bench.
        </p>
        <span className="stamp" style={{transform:'rotate(2deg)'}}>Been There</span>
      </div>
    </section>
  );
}

/* ---------- Case Study ---------- */
function CaseStudy() {
  return (
    <section id="case" data-screen-label="case">
      <div className="sec-head">
        <div className="sec-label">06 · Illustrative Case</div>
        <div>
          <h2>Member <em>019.</em><br/>Second time around.</h2>
          <p className="lead dim" style={{marginTop:28, maxWidth:'52ch'}}>
            A composite, for illustration. Representative of what a typical three-trial engagement looks like end to end.
          </p>
        </div>
      </div>

      <div className="card" style={{padding:0, border:'1px solid var(--border)', background:'var(--surface)'}}>
        <div style={{display:'flex', justifyContent:'space-between', padding:'16px 24px', borderBottom:'1px solid var(--border)', alignItems:'center', flexWrap:'wrap', gap: 12}}>
          <div style={{display:'flex', gap:16, alignItems:'center'}}>
            <span style={{fontSize:10, letterSpacing:3, color:'var(--signal)', fontWeight:700}}>FILE · MBR-019</span>
            <span style={{fontSize:10, color:'var(--fg-dim)'}}>Opened 2026-02-03 · Closed 2026-09-11</span>
          </div>
        </div>

        <div style={{padding: 40, display:'grid', gridTemplateColumns:'1fr 1fr', gap: 48}} className="case-body">
          <div>
            <div style={{fontSize:10, color:'var(--fg-dim)', letterSpacing:3, textTransform:'uppercase', fontWeight:700, marginBottom:8}}>Founder</div>
            <p style={{fontSize:14, color:'var(--fg-bright)'}}>Second-time founder. Prior company shipped consumer hardware without paying demand — same failure mode as the illustrative § hero case file (composite, not the identical entity).</p>

            <div style={{fontSize:10, color:'var(--fg-dim)', letterSpacing:3, textTransform:'uppercase', fontWeight:700, marginBottom:8, marginTop: 24}}>Hypothesis</div>
            <p style={{fontSize:14, color:'var(--fg-bright)', fontFamily:'var(--display)', fontStyle:'var(--display-style)'}}>"Operations teams will pay €99/mo for an AI copilot that writes their weekly reports."</p>

            <div style={{fontSize:10, color:'var(--fg-dim)', letterSpacing:3, textTransform:'uppercase', fontWeight:700, marginBottom:8, marginTop: 24}}>What Trial 01 killed</div>
            <p style={{fontSize:13, color:'var(--fg)'}}>The €99/mo bracket. Ops teams signaled <b style={{color:'var(--signal)'}}>€290/mo seat</b> for the same workflow, plus procurement approval. Trial 01 rewrote the pricing model in 28 days.</p>

            <div style={{fontSize:10, color:'var(--fg-dim)', letterSpacing:3, textTransform:'uppercase', fontWeight:700, marginBottom:8, marginTop: 24}}>What Trial 02 stripped</div>
            <p style={{fontSize:13, color:'var(--fg)'}}>Mechanics: we removed three roadmap bets the founder defended in workshops — not because users complained, but because session depth did not move when those flows were behind feature flags. Two were rebuilt as smaller surfaces; one was deleted. Week-4 return climbed after the cut.</p>

            <div style={{fontSize:10, color:'var(--fg-dim)', letterSpacing:3, textTransform:'uppercase', fontWeight:700, marginBottom:8, marginTop: 24}}>What Trial 03 landed</div>
            <p style={{fontSize:13, color:'var(--fg)'}}>€12,400 in pre-orders from strangers, no discounts. 42 seats across 11 companies. Two inbound from seed funds.</p>
          </div>

          <div>
            <pre className="ascii" style={{color:'var(--fg)', fontSize:12}}>{ASCII.receipt()}</pre>

            <div style={{marginTop: 24, padding: 20, border:'1px solid var(--signal)', background:'var(--bg)'}}>
              <div style={{fontSize:10, letterSpacing:3, color:'var(--signal)', fontWeight:700, textTransform:'uppercase', marginBottom:8}}>Founder, after Trial 03</div>
              <p style={{fontFamily:'var(--display)', fontStyle:'var(--display-style)', fontSize:20, color:'var(--fg-bright)', lineHeight:1.4, margin:0}}>
                "Best money I ever spent before spending any money. The first time, I built for 14 months. This time, I learned the truth in 28 days."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Club / Registry ---------- */
function ClubSection() {
  const [revealed, setRevealed] = useStateP(null);
  const taken = [1, 2, 3];

  const names = {
    1: { name: 'MBR-001 · A.K.', hint: 'founding · Bucharest' },
    2: { name: 'MBR-002 · L.P.', hint: 'founding · Lisbon' },
    3: { name: 'MBR-003 · D.R.', hint: 'founding · Berlin' },
  };

  const claimSeat = (n) => {
    if (taken.includes(n)) return;
    window.__02100.setSeat(n);
    const target = document.getElementById('apply');
    if (target) target.scrollIntoView ? target.scrollIntoView({ behavior: 'smooth', block: 'start' }) : (window.location.hash = 'apply');
  };

  return (
    <section id="club" data-screen-label="club">
      <div className="sec-head">
        <div className="sec-label">08 · The Club</div>
        <div>
          <h2>One hundred<br/>numbered <em>seats.</em></h2>
          <p className="lead dim" style={{marginTop:28, maxWidth:'52ch'}}>
            You do not apply to The Club. You survive the three trials. Your number is your credential. Click an open seat to note the member number you are aiming for — assignment is earned through the trials.
          </p>
        </div>
      </div>

      {/* Registry */}
      <div style={{border:'1px solid var(--border)', padding: 24, background:'var(--surface)'}}>
        <div style={{display:'flex', justifyContent:'space-between', marginBottom: 20, flexWrap:'wrap', gap: 12}}>
          <div style={{fontSize:10, letterSpacing:3, color:'var(--signal)', fontWeight:700, textTransform:'uppercase'}}>Registry · 02100 · members</div>
          <div style={{fontSize:10, color:'var(--fg-dim)', letterSpacing:2}}>{taken.length.toString().padStart(3,'0')} taken · {(100 - taken.length).toString().padStart(3,'0')} open</div>
        </div>

        <div style={{display:'grid', gridTemplateColumns:'repeat(20, 1fr)', gap: 3}} className="reg-grid">
          {Array.from({length: 100}).map((_, i) => {
            const n = i + 1;
            const isTaken = taken.includes(n);
            const label = String(n).padStart(3, '0');
            const baseStyle = {
              aspectRatio: '1',
              background: isTaken ? 'var(--signal)' : 'var(--bg)',
              border: '1px solid ' + (isTaken ? 'var(--signal)' : 'var(--border)'),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--mono)',
              fontSize: 9,
              fontWeight: isTaken ? 800 : 500,
              color: isTaken ? 'var(--bg)' : 'var(--fg-dim)',
              cursor: isTaken ? 'not-allowed' : 'pointer',
              transition: 'background .12s ease, border-color .12s ease, color .12s ease',
              padding: 0,
            };
            if (isTaken) {
              return (
                <div key={n}
                  role="img"
                  aria-label={`Seat ${label} — taken`}
                  onMouseEnter={() => setRevealed(n)}
                  onMouseLeave={() => setRevealed(null)}
                  style={baseStyle}
                >
                  {label}
                </div>
              );
            }
            return (
              <button key={n}
                type="button"
                aria-label={`Claim seat ${label}`}
                onMouseEnter={() => setRevealed(n)}
                onMouseLeave={() => setRevealed(null)}
                onFocus={() => setRevealed(n)}
                onBlur={() => setRevealed(null)}
                onClick={() => claimSeat(n)}
                style={baseStyle}
                onMouseOver={e => { e.currentTarget.style.background = 'var(--signal)'; e.currentTarget.style.borderColor = 'var(--signal)'; e.currentTarget.style.color = 'var(--bg)'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'var(--bg)'; e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--fg-dim)'; }}
              >
                {label}
              </button>
            );
          })}
        </div>

        <div style={{marginTop: 20, padding: 16, border:'1px solid var(--border)', background:'var(--bg)', minHeight: 72, display:'flex', alignItems:'center', justifyContent:'space-between', gap: 24, flexWrap:'wrap'}}>
          <div style={{flex:1, minWidth: 240}}>
            {revealed == null ? (
              <span style={{fontSize:12, color:'var(--fg-dim)', fontStyle:'italic'}}>hover · open seats · preference only until Trial 03</span>
            ) : taken.includes(revealed) ? (
              <div>
                <div style={{fontSize:10, color:'var(--signal)', letterSpacing:3, fontWeight:700}}>OCCUPIED · {names[revealed].name}</div>
                <div style={{fontSize:11, color:'var(--fg-dim)', marginTop:4}}>{names[revealed].hint}</div>
              </div>
            ) : (
              <div>
                <div style={{fontSize:10, color:'var(--signal)', letterSpacing:3, fontWeight:700}}>OPEN · SEAT {String(revealed).padStart(3,'0')} · CLICK TO CLAIM</div>
                <div style={{fontSize:11, color:'var(--fg-dim)', marginTop:4}}>Available to the next founder who clears all three trials.</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Benefits — numbered poster cells with giant numerals */}
      <div style={{marginTop: 48, display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap: 0, border:'1px solid var(--border)'}} className="club-benefits">
        {[
          ['Network',    'One hundred founders who all proved it first. Peer advisory. Warm intros. Deal flow.'],
          ['Credential', '"02100-validated" — a stamp that says your venture survived three trials.'],
          ['Rooms',      'Private dinners. Annual gathering. Workshops. Founders in a room.'],
        ].map(([k, v], i) => (
          <div key={i} style={{borderRight: i < 2 ? '1px solid var(--border)' : 'none', padding: '32px 26px 28px', position:'relative', minHeight: 260, display:'flex', flexDirection:'column'}}>
            <div style={{fontFamily:'var(--mono)', fontSize:10, letterSpacing:3, color:'var(--fg-dim)', fontWeight:700, textTransform:'uppercase'}}>{String(i+1).padStart(2,'0')} · Benefit</div>
            <div style={{fontFamily:'var(--mono)', fontSize: 36, fontWeight:800, color:'var(--signal)', lineHeight:0.9, letterSpacing:-1, marginTop: 10}}>{'0' + (i+1)}</div>
            <div style={{fontFamily:'var(--display)', fontStyle:'var(--display-style)', fontSize: 32, color:'var(--fg-bright)', marginTop: 8, lineHeight: 1}}>{k}</div>
            <div style={{height:1, background:'var(--signal)', width:32, margin:'14px 0'}}></div>
            <p style={{fontSize:12, color:'var(--fg)', margin:0, maxWidth:'none'}}>{v}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
function FAQ() {
  const items = [
    { q: 'If I pass Trial 01 and 02 and fail Trial 03, I\'ve paid €50K and given up six months. What do I actually leave with?',
      a: 'A bill of materials you can hand to another shop or investor: raw research exports (CSV, call logs, transcript index), competitor teardown workbook, pricing matrix with assumptions surfaced, POC repo with branch history and test notes, UX system file, monetization model, architecture overview, GTM narrative draft. Not a vague "insights readout." On a Trial 03 stop, equity does not vest; fee-for-service artifacts transfer per § Pricing.' },
    { q: 'What if the first trial says no?',
      a: 'You saved months versus a build-first path and you leave with Trial 01 outputs only — demand dossier, qualified pipeline notes, recommendation memo. Most founders who stop at T01 re-apply within 12 months with a sharper hypothesis.' },
    { q: 'Is the equity upfront?',
      a: 'No. The 12–18% band is optional and only applies if you elect to scale with 02100 and join The Club. It vests at the end of Trial 03 when we mutually certify delivery against the written scope — "delivery" means the artifact set listed in your engagement letter is in your repos and accounts, not "we tried." Until vesting, you owe fees only; the cap table stays clean. At a qualified Series A, we publish a simple ROFR / buyout formula (15-day match period, fair-market floor from last priced round or independent 409A-style memo). If 02100 dissolves before vesting, unvested interests lapse; if after vesting, your equity converts per the operating agreement with a dissolution waterfall that favors returning operating runway to founders first — full text with cohort.' },
    { q: 'Can I bring my own devs or agency?',
      a: 'No. The methodology depends on the squad. We have seen what happens when founders stitch us into an existing team — it breaks the trial structure. One squad. One verdict chain.' },
    { q: 'What kinds of ideas are out of scope?',
      a: 'Deep hardware with >6mo manufacturing lead time. Regulated clinical / medical device. Pure-science R&D with no reachable end-user within 12 months. Everything else is fair game.' },
    { q: 'Why numbered membership?',
      a: 'Because a stamp only means something if it cannot be handed out. Numbers are permanent. Once 100 is reached, new members enter only when a number opens.' },
    { q: 'What happens if I pass all three?',
      a: 'You are member #N of 100. You own your venture. If you elect the Club path, optional 12–18% vests through delivery at Trial 03 close — not through a pitch negotiation. You get access to the Registry, the rooms, and the priority queue for future 02 100 services.' },
    { q: 'Who runs 02 100?',
      a: 'Sasha Maxim, based in Bucharest. Every squad is composed of Club members — operators who have sat where you sit. Names on request after Discovery call.' },
  ];
  const [open, setOpen] = useStateP(0);
  return (
    <section id="faq" data-screen-label="faq">
      <div className="sec-head">
        <div className="sec-label">09 · Objections</div>
        <div><h2>The things<br/>you are <em>already thinking.</em></h2></div>
      </div>
      <div style={{border:'1px solid var(--border)'}}>
        {items.map((it, i) => {
          const isOpen = open === i;
          const panelId = `faq-panel-${i}`;
          const btnId = `faq-q-${i}`;
          return (
            <div key={i} style={{borderBottom: i < items.length-1 ? '1px solid var(--border)' : 'none'}}>
              <button id={btnId}
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                style={{width:'100%', textAlign:'left', padding:'22px 28px', background: isOpen ? 'var(--surface)' : 'transparent', border:'none', color:'var(--fg-bright)', fontFamily:'var(--display)', fontSize: 20, fontStyle:'var(--display-style)', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'baseline', gap: 24}}>
                <span><span style={{fontFamily:'var(--mono)', fontSize: 10, color:'var(--signal)', letterSpacing:3, fontWeight:700, marginRight: 16, fontStyle:'normal'}}>{String(i+1).padStart(2,'0')}</span>{it.q}</span>
                <span aria-hidden="true" style={{color:'var(--signal)', fontFamily:'var(--mono)', fontSize:14, transform: isOpen ? 'rotate(45deg)' : 'rotate(0)', transition:'transform .2s', display:'inline-block', fontStyle:'normal'}}>+</span>
              </button>
              {isOpen && (
                <div id={panelId} role="region" aria-labelledby={btnId} style={{padding:'0 28px 28px 68px', background:'var(--surface)'}}>
                  <p style={{fontSize:14, color:'var(--fg)', maxWidth:'68ch'}}>{it.a}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ---------- Apply ---------- */
function Apply() {
  const [sent, setSent] = useStateP(false);
  const [form, setForm] = useStateP({ name:'', company:'', stage:'', budget:'', url:'', pitch:'' });

  // cohort countdown
  const target = new Date('2026-05-06T09:00:00Z');
  const [now, setNow] = useStateP(new Date());
  useEffectP(() => { const id = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(id); }, []);
  const diff = Math.max(0, target - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor(diff / 3600000) % 24;
  const m = Math.floor(diff / 60000) % 60;
  const s = Math.floor(diff / 1000) % 60;

  return (
    <section id="apply" data-screen-label="apply" style={{background:'var(--surface)'}}>
      <div className="sec-head">
        <div className="sec-label">10 · Application</div>
        <div>
          <h2>Apply to join<br/>the next <em>cohort.</em></h2>
          <p className="lead dim" style={{marginTop:28, maxWidth:'52ch'}}>
            No pitch decks. No NDAs. A short intake. We reply within 72 hours with a Discovery slot for the upcoming cohort window — or a clean pass.
          </p>
        </div>
      </div>

      <div style={{ marginBottom: 28, padding: '22px 28px', borderLeft: '3px solid var(--signal)', background: 'var(--surface)' }}>
        <p style={{ fontFamily: 'var(--display)', fontStyle: 'var(--display-style)', fontSize: 'clamp(18px, 2.4vw, 24px)', color: 'var(--fg-bright)', margin: 0, lineHeight: 1.4, maxWidth: '62ch' }}>
          If your last company failed because you built for fourteen months and learned the market wasn&apos;t there in month fifteen — this is the six months that was missing.
        </p>
      </div>

      {/* BIG cohort window banner */}
      <div style={{marginBottom: 36, border:'2px solid var(--signal)', background:'var(--bg)', padding:0, overflow:'hidden'}}>
        <div style={{display:'grid', gridTemplateColumns:'1fr 1.4fr 1fr', alignItems:'stretch'}} className="cohort-grid">
          {/* 1. Next Cohort Window + Date */}
          <div style={{padding:'20px 28px', borderRight:'1px solid var(--signal)'}}>
            <div style={{fontSize:10, letterSpacing:3, color:'var(--signal)', fontWeight:800, textTransform:'uppercase'}}>▸ Next cohort window</div>
            <div style={{marginTop: 14, fontFamily:'var(--display)', fontStyle:'var(--display-style)', fontSize: 40, color:'var(--fg-bright)', lineHeight: 1, letterSpacing:-1}}>2026 · 05 · 06</div>
          </div>

          {/* 2. Opens in + Timer */}
          <div style={{padding:'20px 28px', borderRight:'1px solid var(--signal)'}}>
            <div style={{fontSize:10, letterSpacing:3, color:'var(--signal)', fontWeight:800, textTransform:'uppercase'}}>Opens in</div>
            <div style={{marginTop: 12, display:'flex', gap: 8, alignItems:'flex-end', flexWrap:'wrap'}}>
              {[[d,'Days'],[h,'Hrs'],[m,'Min'],[s,'Sec']].map(([v, u], i) => (
                <div key={i} style={{background:'var(--signal)', color:'var(--bg)', padding:'8px 12px', minWidth: 66, textAlign:'center'}}>
                  <div style={{fontFamily:'var(--mono)', fontSize:24, lineHeight:1, fontWeight:800}}>{String(v).padStart(2,'0')}</div>
                  <div style={{fontSize:9, letterSpacing:2, opacity:0.8, marginTop:2}}>{u}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 3. Discovery Slots */}
          <div style={{padding:'20px 28px'}}>
            <div style={{fontSize:10, letterSpacing:3, color:'var(--signal)', fontWeight:800, textTransform:'uppercase'}}>Discovery slots open</div>
            <div style={{marginTop: 10, fontFamily:'var(--mono)', fontSize: 56, color:'var(--signal)', fontWeight:800, letterSpacing:-2, lineHeight:1}}>02<span style={{color:'var(--fg-dim)'}}>/04</span></div>
          </div>
        </div>
      </div>

      <div style={{display:'grid', gridTemplateColumns:'1.2fr 1fr', gap: 48}} className="apply-grid">
        <form onSubmit={(e) => { e.preventDefault(); setSent(true); }} style={{border:'1px solid var(--signal)', padding: 36, background:'var(--bg)'}}>
          <div style={{display:'flex', justifyContent:'space-between', marginBottom: 28, alignItems:'center'}}>
            <div style={{fontSize:10, letterSpacing:3, color:'var(--signal)', fontWeight:700}}>FORM · FIELD-01 · INTAKE</div>
            <div style={{fontSize:10, color:'var(--fg-dim)'}}>Confidential · eyes-only</div>
          </div>

          <Field label="Your name" value={form.name} onChange={v => setForm({...form, name: v})} placeholder="Founder · operator · individual" />
          <Field label="Venture name or codename" value={form.company} onChange={v => setForm({...form, company: v})} placeholder="Can be a placeholder" />
          <Grid2>
            <Select label="Stage" value={form.stage} onChange={v => setForm({...form, stage: v})} options={['Idea only', 'Pre-launch build', 'Launched · no retention', 'Launched · no revenue', 'Rebuilding post-failure']} />
            <Select label="Budget for 6 months" value={form.budget} onChange={v => setForm({...form, budget: v})} options={['< €50K', '€50–95K', '€95–200K', '€200K+']} />
          </Grid2>
          <Field label="URL (site · deck · repo · anything)" value={form.url} onChange={v => setForm({...form, url: v})} placeholder="https://" />
          <Field label="What would a no at Trial 01 save you?" value={form.pitch} onChange={v => setForm({...form, pitch: v})} placeholder="In euros and months. Be brutal." textarea />

          <div style={{display:'flex', gap: 12, marginTop: 24, alignItems:'center', justifyContent:'space-between'}}>
            <p className="small" style={{margin:0, maxWidth: 280}}>By submitting you agree to a 30-min Discovery call. No pitch decks accepted.</p>
            <button type="submit" className="btn primary">Submit application →</button>
          </div>

          {sent && (
            <div style={{marginTop: 24, padding: 20, border:'1px solid var(--signal)', background:'var(--surface)', display:'flex', gap:16, alignItems:'center'}}>
              <span className="stamp" style={{transform:'rotate(-4deg)'}}>Received</span>
              <p style={{margin:0, fontSize:13}}>Field-01 logged. Expect a reply within 72 hours from the squad directly — no assistants, no funnels.</p>
            </div>
          )}
        </form>

        <div>
          <div style={{fontFamily:'var(--logo)', fontSize:'clamp(72px, 10vw, 140px)', fontWeight:800, color:'var(--signal)', letterSpacing:-4, lineHeight:0.9}}>02100</div>
          <div style={{fontFamily:'var(--display)', fontStyle:'var(--display-style)', fontSize: 20, color:'var(--fg)', marginTop: 10, letterSpacing:0}}>Zero to One Hundred</div>
          <div style={{height:2, background:'var(--signal)', width:120, margin:'16px 0 24px'}}/>
          <p style={{fontFamily:'var(--display)', fontStyle:'var(--display-style)', fontSize: 28, color:'var(--fg-bright)', lineHeight:1.3, maxWidth:'16ch'}}>
            Build on proof,<br/>not on hope.
          </p>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, placeholder, textarea }) {
  const id = useIdP();
  const common = {
    id,
    value, onChange: (e) => onChange(e.target.value), placeholder,
    style: {
      width:'100%', background:'transparent', border:'none', borderBottom:'1px solid var(--border)',
      color:'var(--fg-bright)', fontFamily:'var(--mono)', fontSize:14, padding:'10px 0', outline:'none',
      resize:'vertical',
    },
    onFocus: (e) => { e.target.style.borderColor = 'var(--signal)'; },
    onBlur:  (e) => { e.target.style.borderColor = 'var(--border)'; },
  };
  return (
    <div style={{marginBottom: 20}}>
      <label htmlFor={id} style={{display:'block', fontSize:10, letterSpacing:2, color:'var(--fg-dim)', textTransform:'uppercase', fontWeight:700, marginBottom: 4}}>{label}</label>
      {textarea ? <textarea rows={3} {...common} /> : <input type="text" {...common} />}
    </div>
  );
}
function Select({ label, value, onChange, options }) {
  const id = useIdP();
  return (
    <div style={{marginBottom: 20}}>
      <label htmlFor={id} style={{display:'block', fontSize:10, letterSpacing:2, color:'var(--fg-dim)', textTransform:'uppercase', fontWeight:700, marginBottom: 4}}>{label}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} style={{width:'100%', background:'transparent', border:'none', borderBottom:'1px solid var(--border)', color:'var(--fg-bright)', fontFamily:'var(--mono)', fontSize:14, padding:'10px 0', outline:'none'}}>
        <option value="" style={{background:'var(--bg)'}}>—</option>
        {options.map(o => <option key={o} value={o} style={{background:'var(--bg)'}}>{o}</option>)}
      </select>
    </div>
  );
}
function Grid2({ children }) {
  return <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap: 24}}>{children}</div>;
}

/* ---------- Footer ---------- */
function Footer() {
  return (
    <footer style={{borderTop:'1px solid var(--border)', padding:'60px var(--gutter) 40px', position:'relative'}}>
      <div style={{maxWidth:'var(--max-w)', margin:'0 auto'}}>
        <div style={{display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', gap: 48}} className="footer-grid">
          <div>
            <div style={{fontFamily:'var(--logo)', fontSize: 32, fontWeight:800, color:'var(--signal)', letterSpacing:-1}}>02100</div>
            <p style={{fontSize:12, color:'var(--fg-dim)', marginTop: 12, maxWidth:'36ch'}}>Zero to one hundred. A members-only venture club. Prove before you build.</p>
          </div>
          {[
            { h: 'The Method', l: [['Trials', '#trials'], ['Pricing', '#pricing'], ['Squad', '#squad']] },
            { h: 'The Club',   l: [['Registry', '#club'], ['FAQ', '#faq']] },
            { h: 'Reach',      l: [['Apply', '#apply'], ['sasha@02100.club', 'mailto:sasha@02100.club']] },
          ].map(col => (
            <div key={col.h}>
              <div style={{fontSize:10, letterSpacing:3, color:'var(--fg-dim)', fontWeight:700, textTransform:'uppercase', marginBottom: 14}}>{col.h}</div>
              {col.l.map(([t,u]) => <div key={t}><a href={u} style={{color:'var(--fg)', textDecoration:'none', fontSize:13}} onMouseOver={e => e.target.style.color='var(--signal)'} onMouseOut={e => e.target.style.color='var(--fg)'}>{t}</a></div>)}
            </div>
          ))}
        </div>
        <div style={{marginTop: 48, paddingTop: 20, borderTop:'1px solid var(--border)', display:'flex', justifyContent:'space-between', fontSize:10, color:'var(--fg-dim)', letterSpacing:2, textTransform:'uppercase', flexWrap:'wrap', gap: 12}}>
          <span>© 02100 · Bucharest · 2026</span>
          <span>Confidential · V1.0</span>
          <span>Capped at 100 · Scarcity is structural</span>
        </div>
      </div>
    </footer>
  );
}

export { PricingSection, SquadSection, CaseStudy, ClubSection, FAQ, Apply, Footer };
